import { useMemo } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import type { ChartSegmentLabels } from '@digital-twins/deck-ui/components/chart-segment/ChartSegmentContext'
import type { TextResponsesListLabels } from '@digital-twins/deck-ui/components/data-display/TextResponsesList/TextResponsesList'
import { LightboxLabelsProvider } from '@digital-twins/deck-ui/components/primitives/LightboxImage/LightboxImage'
import type { SlideErrorBoundaryLabels } from '@digital-twins/deck-ui/slides/SlideErrorBoundary'

/**
 * deck-ui's viewer chrome (plots plan Stage 2, owner decision 7): the segment
 * gear, the slide error fallback, the image preview and the open-end list are
 * the viewer's interface, not slide content, so their words come from the
 * app's own translations in the viewer's UI language. deck-ui ships none.
 */

export function useChartSegmentLabels(): ChartSegmentLabels {
  const { t } = useTranslation('app')
  return useMemo(
    () => ({
      offWholeSample: t('studyReport.deckChrome.segment.offWholeSample'),
      offShowsSplit: t('studyReport.chartSegmentOffShowsSplit'),
      breakDownPerSegment: t('studyReport.deckChrome.segment.breakDownPerSegment'),
      breakDownBy: t('studyReport.deckChrome.segment.breakDownBy'),
      breakDown: t('studyReport.deckChrome.segment.breakDown'),
      axesPartlyUnavailable: t('studyReport.deckChrome.segment.axesPartlyUnavailable'),
      axesUnavailable: t('studyReport.deckChrome.segment.axesUnavailable'),
      retry: t('studyReport.deckChrome.segment.retry'),
      demographics: t('studyReport.deckChrome.segment.demographics'),
      yourSegments: t('studyReport.deckChrome.segment.yourSegments'),
      groupCount: (count: number) => t('studyReport.deckChrome.segment.groupCount', { count }),
      loading: t('studyReport.deckChrome.segment.loading'),
      viewFailed: t('studyReport.deckChrome.segment.viewFailed'),
      noBreakdown: (axisLabel: string) => t('studyReport.deckChrome.segment.noBreakdown', { label: axisLabel }),
      thisSegment: t('studyReport.deckChrome.segment.thisSegment'),
      tabs: t('studyReport.deckChrome.segment.tabs'),
      showMoreSegments: t('studyReport.deckChrome.segment.showMoreSegments'),
      showPreviousSegments: t('studyReport.deckChrome.segment.showPreviousSegments'),
    }),
    [t],
  )
}

export function useSlideErrorBoundaryLabels(): SlideErrorBoundaryLabels {
  const { t } = useTranslation('app')
  return useMemo(
    () => ({
      title: t('studyReport.deckChrome.slideError.title'),
      message: t('studyReport.deckChrome.slideError.message'),
      retry: t('studyReport.deckChrome.slideError.retry'),
      errorDetails: t('studyReport.deckChrome.slideError.errorDetails'),
    }),
    [t],
  )
}

export function useTextResponsesListLabels(): TextResponsesListLabels {
  const { t } = useTranslation('app')
  return useMemo(
    () => ({
      noThemesTitle: t('studyReport.deckChrome.textResponses.noThemesTitle'),
      noThemesMessage: t('studyReport.deckChrome.textResponses.noThemesMessage'),
      themes: t('studyReport.deckChrome.textResponses.themes'),
      raw: t('studyReport.deckChrome.textResponses.raw'),
      noResponses: t('studyReport.deckChrome.textResponses.noResponses'),
      responseNumber: (number: number) => t('studyReport.deckChrome.textResponses.responseNumber', { number }),
    }),
    [t],
  )
}

/**
 * The image preview sits deep inside deck-ui's plots, so the app supplies its
 * copy once, at the root, for every surface that draws a plot.
 */
export function AppLightboxLabelsProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('app')
  const labels = useMemo(
    () => ({
      imagePreview: t('studyReport.deckChrome.lightbox.imagePreview'),
      closePreview: t('studyReport.deckChrome.lightbox.closePreview'),
    }),
    [t],
  )
  return <LightboxLabelsProvider labels={labels}>{children}</LightboxLabelsProvider>
}
